import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  clinicSettingsApi,
  clinicSettingsQueryKeys,
  parseWorkingHours,
  type ClinicSettingsUpdate,
  type DayHours,
  type DayKey,
  type WorkingHours,
} from "../../shared/api/clinic";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { HolidaysPanel } from "./components/HolidaysPanel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeInput } from "../../shared/ui/TimeInput";

/** T81 — Cài đặt phòng khám (tên, địa chỉ, liên hệ, mã số thuế, giờ làm việc). */

const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Thứ Hai" },
  { key: "tue", label: "Thứ Ba" },
  { key: "wed", label: "Thứ Tư" },
  { key: "thu", label: "Thứ Năm" },
  { key: "fri", label: "Thứ Sáu" },
  { key: "sat", label: "Thứ Bảy" },
  { key: "sun", label: "Chủ Nhật" },
];

const DEFAULT_HOURS: DayHours = { open: "08:00", close: "17:00" };

export function ClinicSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
  });

  const [form, setForm] = useState<ClinicSettingsUpdate>({ name: "" });
  const [hours, setHours] = useState<WorkingHours>({});
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number>(0);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      name: query.data.name,
      address: query.data.address,
      phone: query.data.phone,
      email: query.data.email,
      taxCode: query.data.taxCode,
      facilityRegistrationNo: query.data.facilityRegistrationNo,
    });
    setHours(parseWorkingHours(query.data.workingHoursJson));
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      clinicSettingsApi.update({ ...form, workingHours: hours }),
    onSuccess: (data) => {
      qc.setQueryData(clinicSettingsQueryKeys.current(), data);
      setSavedAt(Date.now());
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  function patch<K extends keyof ClinicSettingsUpdate>(
    k: K,
    v: ClinicSettingsUpdate[K]
  ) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function setDayClosed(day: DayKey, closed: boolean) {
    setHours((h) => ({ ...h, [day]: closed ? null : (h[day] ?? DEFAULT_HOURS) }));
  }
  function setDayHours(day: DayKey, field: keyof DayHours, val: string) {
    setHours((h) => {
      const cur = h[day] ?? DEFAULT_HOURS;
      return { ...h, [day]: { ...cur, [field]: val } };
    });
  }

  if (query.isLoading)
    return <p className="text-muted-foreground">Đang tải…</p>;
  if (query.isError)
    return (
      <p className="text-destructive">
        Không tải được cài đặt phòng khám.
      </p>
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title="Cài đặt phòng khám"
        description={
          <>
            Thông tin định danh phòng khám — hiển thị trên hoá đơn, đơn thuốc
            và các tài liệu in. Mã{" "}
            <span className="font-mono">{query.data?.code}</span>.
          </>
        }
        actions={
          <Button
            type="submit"
            disabled={save.isPending || !form.name?.trim()}
          >
            {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
          </Button>
        }
      />

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
      {savedAt > 0 && Date.now() - savedAt < 3000 && (
        <p className="mb-3 flex items-center gap-1 text-sm text-green-700">
          <Check className="h-4 w-4" />
          Đã lưu
        </p>
      )}

      <Card className="mt-3 max-w-[880px]">
        <CardContent className="p-6">
          <CardTitle className="mb-4">Thông tin chung</CardTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tên phòng khám *" required>
              <Input
                value={form.name ?? ""}
                onChange={(e) => patch("name", e.target.value)}
                required
              />
            </Field>
            <Field label="Số điện thoại">
              <Input
                type="tel"
                value={form.phone ?? ""}
                onChange={(e) => patch("phone", e.target.value)}
                placeholder="VD: 0901234567"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email ?? ""}
                onChange={(e) => patch("email", e.target.value)}
              />
            </Field>
            <Field label="Mã số thuế">
              <Input
                value={form.taxCode ?? ""}
                onChange={(e) => patch("taxCode", e.target.value)}
              />
            </Field>
            <Field label="Số giấy phép hoạt động Bộ Y Tế">
              <Input
                value={form.facilityRegistrationNo ?? ""}
                onChange={(e) =>
                  patch("facilityRegistrationNo", e.target.value)
                }
              />
            </Field>
            <div className="hidden sm:block" />
            <Field label="Địa chỉ" full>
              <Textarea
                rows={2}
                value={form.address ?? ""}
                onChange={(e) => patch("address", e.target.value)}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-3 max-w-[880px]">
        <CardContent className="p-6">
          <CardTitle className="mb-2">Giờ làm việc</CardTitle>
          <p className="mb-3 text-sm text-muted-foreground">
            Đặt khung giờ làm việc theo tuần. Bỏ chọn "Nghỉ" để cấu hình giờ mở –
            giờ đóng cho ngày tương ứng.
          </p>
          {/*
            #18860 — "Nghỉ" chặn theo NGÀY. #19938 — giờ mở – đóng là TRẦN thời gian: không kênh
            nào đặt được ngoài giờ, và Buổi khám / Lịch làm việc vượt giờ không lưu được (trừ Buổi
            ngoài giờ). Một thứ chưa khai giờ vẫn là không trần — phòng khám chưa dùng màn này không
            bị ảnh hưởng.
          */}
          <p className="mb-3 rounded border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm">
            Tick <strong>"Nghỉ"</strong> sẽ <strong>chặn đặt lịch</strong> vào thứ đó hằng tuần.
            Giờ mở – đóng là <strong>giới hạn thời gian</strong> của phòng khám: không đặt được lịch
            ngoài khung này, và Buổi khám / Lịch làm việc vượt khung sẽ không lưu được — trừ buổi có
            cờ <strong>Nhận lịch ngoài Giờ làm việc</strong>. Muốn chặn một ngày cụ thể, dùng{" "}
            <strong>Ngày nghỉ</strong> bên dưới.
          </p>
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ngày</TableHead>
                  <TableHead className="w-[100px]">Trạng thái</TableHead>
                  <TableHead className="w-[140px]">Giờ mở</TableHead>
                  <TableHead className="w-[140px]">Giờ đóng</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DAYS.map((d) => {
                  const v = hours[d.key];
                  const closed = v === null;
                  // #19938 — thứ CHƯA KHAI là "không giới hạn giờ", và màn hình phải nói đúng như
                  // vậy: hiện giờ mặc định ở đây là hứa một giới hạn mà hệ thống không áp (giá trị
                  // đó không được lưu). Ô trống; nhập giờ là khai cho thứ đó.
                  const unset = v === undefined;
                  const cur = v ?? { open: "", close: "" };
                  return (
                    <TableRow key={d.key}>
                      <TableCell>
                        {d.label}
                        {unset && (
                          <span
                            data-testid={`hours-unset-${d.key}`}
                            className="block text-xs text-muted-foreground"
                          >
                            Chưa cấu hình — không giới hạn giờ
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={closed}
                            onCheckedChange={(c) =>
                              setDayClosed(d.key, c === true)
                            }
                          />
                          Nghỉ
                        </label>
                      </TableCell>
                      <TableCell>
                        <TimeInput
                          aria-label={`Giờ mở ${d.label}`}
                          value={cur.open}
                          disabled={closed}
                          placeholder="--:--"
                          onChange={(v) => setDayHours(d.key, "open", v)}
                        />
                      </TableCell>
                      <TableCell>
                        <TimeInput
                          aria-label={`Giờ đóng ${d.label}`}
                          value={cur.close}
                          disabled={closed}
                          placeholder="--:--"
                          onChange={(v) => setDayHours(d.key, "close", v)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/*
        #18860 — "khi nào phòng khám đóng cửa" answered on one screen: recurring weekdays above,
        specific dates here. They used to live in different settings groups.
      */}
      <Card className="mt-3 max-w-[880px]">
        <CardContent className="p-6">
          <CardTitle className="mb-2">Ngày nghỉ</CardTitle>
          <p className="mb-3 text-sm text-muted-foreground">
            Những ngày phòng khám đóng cửa (Tết, lễ, nghỉ đột xuất). Hệ thống{" "}
            <strong>chặn đặt lịch</strong> vào các ngày này — áp dụng cho mọi bác sĩ và cả lịch
            "bác sĩ bất kỳ".
          </p>
          <HolidaysPanel />
        </CardContent>
      </Card>
    </form>
  );
}

function Field({
  label,
  full,
  required,
  children,
}: {
  label: string;
  full?: boolean;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
